import { useForm, SubmitHandler } from 'react-hook-form';
import { useEffect, useState } from 'react';
import { useBusinessUnitRequest } from './useBusinessUnitRequest';
import { BusinessUnit, BusinessUnitForm } from 'data/BusinessUnit';
import Vendor from 'data/Vendor';
import { useAuth } from 'lib/Auth';
import { isVendorOnly } from 'data/User';
import FeatureFlag from 'data/FeatureFlag';
import { loadBrands } from 'requests/brand';
import { useAPI } from 'lib/API';
import Brand from 'data/Brand';

export const useBusinessUnitForm = (selectedVendor: Vendor, businessUnit?: BusinessUnit, featureFlags?: FeatureFlag) => {

    const [showCreateModal, setShowCreateModal] = useState(false);
    const [reloadListing, setReloadListing] = useState(false);
    const [brands, setBrands] = useState<any[]>([]);

    const { user } = useAuth();
    const disableFormFields = isVendorOnly(user!);

    const formDefaultValues: BusinessUnitForm = {
        id: businessUnit?.id,
        name: businessUnit?.name || '',
        vendorId: businessUnit?.vendorId,
        brandId: businessUnit?.brandId,
        isSubscriptionEnabled: businessUnit?.isSubscriptionEnabled || false,
    };

    const {
        control,
        handleSubmit,
        watch,
        formState: { errors, isValid, isDirty },
        reset,
        setValue,
        getValues,
        setError,
        clearErrors,
        trigger,
    } = useForm<BusinessUnitForm>({
        defaultValues: formDefaultValues,
        mode: 'onBlur',
        shouldFocusError: true,
    });

    const { updateBusinessUnitReq, createBusinessUnitReq } = useBusinessUnitRequest({ setShowCreateModal, setReloadListing, reset });

    const brandsReq = useAPI({ deferFn: loadBrands, onResolve: (data: Brand[]) => { setBrands(data); } });

    useEffect(() => {
        brandsReq.run(selectedVendor ? selectedVendor.id : null)
    }, [selectedVendor]);

    // Reset form if `businessUnit` prop changes
    useEffect(() => {
        reset(formDefaultValues);
    }, [businessUnit, reset]);

    const onSubmit: SubmitHandler<BusinessUnitForm> = async (data) => {
        data?.id ? updateBusinessUnitReq.run(data) : createBusinessUnitReq.run(data);
    };

    const isLoading = updateBusinessUnitReq.isLoading || createBusinessUnitReq.isLoading || brandsReq.isLoading;

    const vendorBrands = brands?.filter(
        (brand: any) => brand.brandVendor?.vendorId === selectedVendor?.id
    ).map((brand: any) => ({
        ...brand,
        isAssigned: brand.isAssigned && brand.id !== businessUnit?.brandId
    }));

    return {
        control,
        handleSubmit,
        watch,
        errors,
        isValid,
        onSubmit,
        setValue,
        getValues,
        isLoading,
        setError,
        clearErrors,
        trigger,
        isDirty,
        vendorBrands,
        reloadListing,
        setReloadListing,
        reset,
        disableFormFields,
        showCreateModal,
        setShowCreateModal,
    };
};